// import "src/devtools/wdyr" // Temporarily disabled for React 19 compatibility

import { PropsWithChildren, useEffect, useMemo } from "react"
import {
  LogBox,
  NativeModules,
  Platform,
  UIManager,
  useColorScheme,
  View,
  ViewStyle,
} from "react-native"

import { ThemeProvider as ExpoRouterThemeProvider } from "@react-navigation/native"
import {
  createTheme as createRNEUITheme,
  ThemeProvider as RNEUIThemeProvider,
} from "@rn-vui/themed"
import * as Sentry from "@sentry/react-native"
import { QueryClient } from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import Constants, { ExecutionEnvironment } from "expo-constants"
import { useFonts } from "expo-font"
import { Slot, useNavigationContainerRef, usePathname } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import * as SplashScreen from "expo-splash-screen"
import { StatusBar } from "expo-status-bar"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { KeyboardProvider } from "react-native-keyboard-controller"
import { configureReanimatedLogger, ReanimatedLogLevel } from "react-native-reanimated"
import { SafeAreaProvider, useSafeAreaInsets } from "react-native-safe-area-context"
import Toast from "react-native-toast-message"

import { ChannelLinkCapture } from "src/components/ChannelLinkCapture"
import { ErrorBoundary } from "src/components/ErrorBoundary/ErrorBoundary"
import { Loading } from "src/components/Loading"
import { MobileAppBanner } from "src/components/MobileAppBanner"
import PostHogWrapper from "src/components/PostHogWrapper"
import { WebOnlyOverlays } from "src/components/WebOnlyOverlays"
import { getEnvExtraVar } from "src/config/config.base"
import { AuthProvider, useAuth } from "src/context/AuthContext"
import { AvatarModeProvider } from "src/context/AvatarModeContext"
import { ChannelLinkProvider } from "src/context/ChannelLinkContext"
import { RiveProvider } from "src/context/RiveContext"
import { useThemeProvider } from "src/hooks/useAppTheme"
import { useExpoAvSetup } from "src/hooks/useExpoAvSetup"
import { useWebAppUpdatePrompt } from "src/hooks/useWebAppUpdatePrompt"
import { useWebViewportLock } from "src/hooks/useWebViewportLock"
import { useInitialRootStore } from "src/models"
import { colorsDark, colorsLight, customFontsToLoad } from "src/theme"
import { ExpoRouterLightTheme } from "src/theme/colors"
import { ExpoRouterDarkTheme } from "src/theme/colorsDark"
import { typography } from "src/theme/typography"
import { storage } from "src/utils/storage/mmkv"
import { createPersister } from "src/utils/tanstackQueryPersister"

// Clear Android cookies on startup to prevent OkHttp cookie parsing crash
// The error `java.lang.StringIndexOutOfBoundsException` occurs when a malformed cookie
// is stored in the cookie jar. This must happen BEFORE any network requests.
if (Platform.OS === "android") {
  try {
    const CookieManager = NativeModules.CookieManager
    if (CookieManager?.clearAll) {
      CookieManager.clearAll()
        .then(() => console.log("[_layout] Android cookies cleared on startup"))
        .catch((e: Error) => console.warn("[_layout] Failed to clear Android cookies:", e))
    }
  } catch (e) {
    console.warn("[_layout] Error accessing CookieManager:", e)
  }
}

// Disable Reanimated strict mode on web to silence "Reading from value during component render"
// (e.g. when AvatarOverlay/Rive uses animated styles on web, the lib can read shared values during render)
if (Platform.OS === "web") {
  try {
    configureReanimatedLogger({
      level: ReanimatedLogLevel.warn,
      strict: false,
    })
  } catch {
    // Reanimated may not be fully available on web in some setups
  }
}

// React Native Reanimated Web polyfill - ONLY for web platform
if (Platform.OS === "web" && typeof global !== "undefined") {
  // Polyfill for React Native Reanimated on web
  if (typeof (global as any)._WORKLET === "undefined") {
    ;(global as any)._WORKLET = false
  }
  if (typeof (global as any).__reanimatedWorkletInit === "undefined") {
    ;(global as any).__reanimatedWorkletInit = () => {}
  }
  if (typeof (global as any)._getAnimationTimestamp === "undefined") {
    ;(global as any)._getAnimationTimestamp = () => performance.now()
  }
  if (typeof (global as any)._setAndGetAnimationTimestamp === "undefined") {
    ;(global as any)._setAndGetAnimationTimestamp = () => performance.now()
  }
  if (typeof (global as any).__reanimatedModuleProxy === "undefined") {
    ;(global as any).__reanimatedModuleProxy = {
      _updateDataSynchronously: () => {},
      scheduleOnUI: (worklet: any) => worklet(),
      createAnimatedComponent: (component: any) => component,
      configureReanimatedLogger: () => {},
      enableLayoutAnimations: () => {},
      getViewProp: () => undefined,
      registerEventHandler: () => {},
      unregisterEventHandler: () => {},
      configureProps: () => {},
      installCoreFunctions: () => {},
      makeShareableClone: (value: any) => value,
    }
  }
  // Additional polyfills for Reanimated timing functions
  if (typeof (global as any).__getCurrentTime === "undefined") {
    ;(global as any).__getCurrentTime = () => performance.now()
  }
  // Polyfill for Reanimated logger configuration
  if (typeof (global as any).__reanimatedLoggerConfig === "undefined") {
    ;(global as any).__reanimatedLoggerConfig = {
      level: 0, // 0 = no logging, 1 = errors, 2 = warnings, 3 = info
      strict: false,
      logFunction: () => {},
    }
  }
  // Additional logger polyfills
  if (typeof (global as any).__DEV__ === "undefined") {
    ;(global as any).__DEV__ = __DEV__
  }
  // Additional polyfills for shared values and animations
  if (typeof (global as any).__callGuardSafe === "undefined") {
    ;(global as any).__callGuardSafe = (fn: any) => fn
  }
  if (typeof (global as any).__ErrorUtils === "undefined") {
    ;(global as any).__ErrorUtils = {
      reportFatalError: (error: any) => {
        console.error("Fatal Error:", error)
      },
    }
  }
  // Additional Reanimated native module polyfills
  if (typeof (global as any).InnerNativeModule === "undefined") {
    ;(global as any).InnerNativeModule = {
      makeShareableClone: (value: any) => value,
    }
  }
}

if (__DEV__) {
  // Load Reactotron configuration in development. We don't want to
  // include this in our production bundle, so we are using `if (__DEV__)`
  // to only execute this in development.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require("src/devtools/ReactotronConfig.ts")

  LogBox.ignoreLogs(['getHost: "Invalid non-string URL" for scriptURL - Falling back to localhost'])
}

// Construct a new integration instance. This is needed to communicate between the integration and React
const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: Constants.executionEnvironment === ExecutionEnvironment.StoreClient, // Only in native builds, not in Expo Go.
})

Sentry.init({
  dsn: "https://ef446309fd4a05188d74b3a5bcb91c77@o4508337499865088.ingest.us.sentry.io/4508337526669312",
  // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing.
  // We recommend adjusting this value in production.
  tracesSampleRate: __DEV__ ? 1.0 : 0.2,
  debug: __DEV__, // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production
  integrations: [navigationIntegration],
  enableNativeFramesTracking: Constants.executionEnvironment === ExecutionEnvironment.StoreClient, // Only in native builds, not in Expo Go.
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // gcTime = how long unused data should be kept in cache
      gcTime: 1000 * 60 * 60 * 24 * 5, // 5 days
      staleTime: 1000 * 60 * 5, // 5 min
    },
  },
})
const persister = createPersister("tanstack-query-cache")

// Enable LayoutAnimation on Android
if (Platform.OS === "android" && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true)
}

SplashScreen.preventAutoHideAsync()

const hideSplashScreen = () => {
  SplashScreen.hideAsync()
}

function RootLayout() {
  const [areFontsLoaded, fontError] = useFonts(customFontsToLoad)
  const { themeScheme, themePreference, setThemeContextOverride, ThemeProvider } =
    useThemeProvider()
  const { bottom } = useSafeAreaInsets()
  // Capture the NavigationContainer ref and register it with the integration.
  const ref = useNavigationContainerRef()

  useEffect(() => {
    // Lock portrait on native only. On web, screen.orientation.lock() is not available
    // and would throw NotSupportedError; skip to avoid console errors.
    if (Platform.OS === "web") return
    ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {
      // Ignore: not supported on iOS Safari or when not fullscreen on Android
    })
  }, [])

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref)
    }
  }, [ref])

  useExpoAvSetup() // This is a side-effect hook. It sets up the audio mode for the app.
  useWebViewportLock() // Web only: prevent iOS Safari from scrolling the body when the keyboard opens.
  useWebAppUpdatePrompt() // Web only: prompt the user to reload when a newer build is deployed.

  // Build a cache buster from the active API host so that persisted queries are
  // discarded whenever the environment changes (e.g. switching between test2 →
  // dev). Without this, dehydrated queries cached against the old host are
  // re-executed with the new session's auth tokens, causing 401s and auto-logout.
  // LOCAL_API_HOST takes precedence (dev override), otherwise fall back to the
  // MMKV-persisted host that AuthContext resolves after login.
  const localApiHost = useMemo(() => getEnvExtraVar("LOCAL_API_HOST"), [])
  const persistedApiHost = storage.getString("apiHost")
  const queryCacheBuster = localApiHost || persistedApiHost || "default"

  const { rehydrated, rootStore: _rootStore } = useInitialRootStore(() => {
    // This runs after the root store has been initialized and rehydrated.

    // If your initialization scripts run very fast, it's good to show the splash screen for just a bit longer to prevent flicker.
    // Slightly delaying splash screen hiding for better UX; can be customized or removed as needed,
    // Note: (vanilla Android) The splash-screen will not appear if you launch your app via the terminal or Android Studio. Kill the app and launch it normally by tapping on the launcher icon. https://stackoverflow.com/a/69831106
    // Note: (vanilla iOS) You might notice the splash-screen logo change size. This happens in debug/development mode. Try building the app for release.
    setTimeout(hideSplashScreen, 500)
  })

  useEffect(() => {
    if (areFontsLoaded || fontError) {
      SplashScreen.hideAsync()
    }
  }, [areFontsLoaded, fontError])

  // Expo Router uses Error Boundaries to catch errors in the navigation tree.
  useEffect(() => {
    if (fontError) throw fontError
  }, [fontError])

  const systemColorScheme = useColorScheme()
  const resolvedTheme = themeScheme ?? (systemColorScheme === "light" ? "light" : "dark")
  const themeColors = resolvedTheme === "light" ? colorsLight : colorsDark
  const navigationTheme = resolvedTheme === "light" ? ExpoRouterLightTheme : ExpoRouterDarkTheme

  useEffect(() => {
    if (Platform.OS !== "web") return
    const styleId = "autofill-theme-overrides"
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null
    if (!styleEl) {
      styleEl = document.createElement("style")
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = `
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px ${themeColors.inputBackground} inset !important;
        -webkit-text-fill-color: ${themeColors.inputText} !important;
        caret-color: ${themeColors.inputText};
      }
    `
  }, [themeColors.inputBackground, themeColors.inputText])

  useEffect(() => {
    if (Platform.OS !== "web") return
    const styleId = "scrollbar-theme-overrides"
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null
    if (!styleEl) {
      styleEl = document.createElement("style")
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }
    const scheme = resolvedTheme === "dark" ? "dark" : "light"
    // avatarControlButtonBackground: #404040 (dark) / #E4E4E4 (light) — neutral grays that suit scrollbar thumbs in both modes
    // sectionSeparator: #808080 in both modes — a neutral mid-gray for the hover state
    const thumbColor = themeColors.avatarControlButtonBackground
    const thumbHoverColor = themeColors.sectionSeparator
    styleEl.textContent = `
      html {
        color-scheme: ${scheme};
        background-color: ${themeColors.background};
        /* Use the stable layout-viewport height (= window.innerHeight) rather
           than 100dvh (dynamic viewport height that shrinks when the iOS Safari
           keyboard opens). Without this, Expo's root tree ends up constrained
           to vv.height (~377px with keyboard) and our maxHeight constraint on
           KeyboardAvoidingView can never grow the element to fill the full
           visible area above the keyboard. */
        height: 100%;
      }
      body {
        /* !important overrides the inline style that expo-system-ui sets on
           document.body via SystemUI.setBackgroundColorAsync when it defaults
           to the dark theme before a user preference is loaded. Without it,
           the inline style wins (specificity 1000 vs our rule's 10) and the
           body stays dark even when the app renders in light mode, producing
           a black bar in the gap between app content and the keyboard. */
        background-color: ${themeColors.background} !important;
        /* Keep the body anchored to the stable layout-viewport height so that
           100dvh-based children do not shrink when the keyboard opens on iOS.
           !important is needed to override any Expo-generated CSS that may use
           100dvh (dynamic viewport height, which shrinks when the keyboard opens). */
        height: 100% !important;
      }
      #root {
        background-color: ${themeColors.background};
        /* Same rationale as body above — must beat Expo's 100dvh rule. */
        height: 100% !important;
      }
      * {
        scrollbar-width: thin;
        scrollbar-color: ${thumbColor} ${themeColors.background};
      }
      ::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }
      ::-webkit-scrollbar-track {
        background: ${themeColors.background};
      }
      ::-webkit-scrollbar-thumb {
        background-color: ${thumbColor};
        border-radius: 4px;
        border: 2px solid ${themeColors.background};
      }
      ::-webkit-scrollbar-thumb:hover {
        background-color: ${thumbHoverColor};
      }
    `
  }, [
    resolvedTheme,
    themeColors.avatarControlButtonBackground,
    themeColors.sectionSeparator,
    themeColors.background,
  ])

  const RNEUITheme = createRNEUITheme({
    mode: themeScheme,
    lightColors: {
      primary: colorsLight.primary,
    },
    darkColors: {
      primary: colorsDark.primary,
    },
    components: {
      Text: {
        style: {
          color: themeColors.text,
          fontFamily: typography.primary.normal,
        },
      },
      Button: {
        type: "outline",
        radius: "md",
        style: {
          backgroundColor: themeColors.primary,
        },
      },
      ListItem: {
        containerStyle: {
          backgroundColor: themeColors.palette.skListItemBackground,
        },
      },
      ListItemTitle: {
        style: {
          color: themeColors.text,
          fontFamily: typography.primary.normal,
        },
      },
      ListItemSubtitle: {
        style: {
          color: themeColors.text,
          fontFamily: typography.primary.normal,
        },
      },
    },
  })

  // Before we show the app, we have to wait for our state to be ready.
  // In the meantime, don't render anything. This will be the background
  // color set in native by rootView's background color.
  // In iOS: application:didFinishLaunchingWithOptions:
  // In Android: https://stackoverflow.com/a/45838109/204044
  // You can replace with your own loading component if you wish.
  if (!rehydrated || !areFontsLoaded) return null

  return (
    <KeyboardProvider>
      <StatusBar style={resolvedTheme === "light" ? "dark" : "light"} />
      <ThemeProvider value={{ themeScheme, themePreference, setThemeContextOverride }}>
        <ExpoRouterThemeProvider value={navigationTheme}>
          <RNEUIThemeProvider theme={RNEUITheme}>
            <SafeAreaProvider style={{ backgroundColor: themeColors.background }}>
              <GestureHandlerRootView style={$flex}>
                <LayoutWrapper>
                  <PersistQueryClientProvider
                    client={queryClient}
                    persistOptions={{
                      persister,
                      // When the API host changes (e.g. switching environments), discard
                      // the entire persisted cache so stale cross-environment queries
                      // cannot cause 401s and auto-logout on the next app launch.
                      buster: queryCacheBuster,
                      dehydrateOptions: {
                        // Only persist queries that have successfully resolved. Persisting
                        // pending/fetching queries causes a hydration mismatch error when a
                        // role switch (or any context change) cancels an in-flight request
                        // after it has already been snapshotted as "pending" by the persister.
                        // Conversation queries are also excluded because they are already
                        // persisted via custom MMKV keys and would blow the localStorage quota.
                        shouldDehydrateQuery: (query) =>
                          query.state.status === "success" && query.queryKey[0] !== "conversations",
                      },
                    }}
                  >
                    <ErrorBoundary catchErrors={"always"}>
                      <ChannelLinkProvider>
                        <AuthProvider>
                          <AvatarModeProvider>
                            <RiveProvider>
                              <AuthLoader>
                                <PostHogWrapper>
                                  <ChannelLinkCapture />
                                  <MobileAppBanner />
                                  <Slot />
                                </PostHogWrapper>
                              </AuthLoader>
                            </RiveProvider>
                          </AvatarModeProvider>
                        </AuthProvider>
                      </ChannelLinkProvider>
                      <Toast position="bottom" bottomOffset={bottom} />
                      <WebOnlyOverlays />
                    </ErrorBoundary>
                  </PersistQueryClientProvider>
                </LayoutWrapper>
              </GestureHandlerRootView>
            </SafeAreaProvider>
          </RNEUIThemeProvider>
        </ExpoRouterThemeProvider>
      </ThemeProvider>
    </KeyboardProvider>
  )
}

function AuthLoader({ children }: PropsWithChildren) {
  const { initialAuthStateLoaded } = useAuth()

  if (!initialAuthStateLoaded) {
    return <Loading message="Signing in…" />
  }

  return children
}

// Routes that should expand to fill the full browser width instead of being
// capped at the phone-canvas max-width.
// /web-browser hosts an embedded website that benefits from the wider canvas,
// matching the content library's full-width treatment.
const FULL_WIDTH_ROUTES = ["/content-library", "/unauth/channel-library", "/web-browser"]

function LayoutWrapper(props: PropsWithChildren) {
  const pathname = usePathname()

  if (Platform.OS === "web") {
    const isFullWidth =
      Platform.OS === "web" &&
      FULL_WIDTH_ROUTES.some((route) => pathname === route || pathname.startsWith(route + "/"))

    // nativeID lets web-only hooks traverse up to this boundary element
    // (e.g. to set overflow: visible on the ancestor chain for the chat scrollbar).
    return (
      <View nativeID="sk-layout-wrapper" style={[$flex, isFullWidth ? $webLayoutWide : $webLayout]}>
        {props.children}
      </View>
    )
  }

  return props.children
}

const $flex: ViewStyle = { flex: 1 }
const $webLayout: ViewStyle = {
  maxWidth: 720,
  marginVertical: 0,
  marginHorizontal: "auto",
  width: "100%",
}
const $webLayoutWide: ViewStyle = {
  maxWidth: 1080,
  marginVertical: 0,
  marginHorizontal: "auto",
  width: "100%",
}

// Export the wrapped component
export default Sentry.wrap(RootLayout)
